import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { PhoneCall, Delete, PhoneOff, Grid3X3, Mic, MicOff, Phone, PhoneOutgoing, ChevronDown, User, ShieldCheck, Zap, RefreshCw, EyeOff } from 'lucide-react';
import { usePhoneInput } from '@/hooks/usePhoneInput';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useRootConfig } from '@/contexts/RootConfigContext';
import { useKeyboardHandler } from '@/hooks/useKeyboardHandler';
import { accountQueries } from '@/services/accountApi';
import { autoTopupQueries } from '@/services/autoTopupApi';
import { useSessionSetting } from '@/hooks/useSetting';
import { useAuth } from '@/contexts/AuthContext';
import { useVoiceServiceContext } from '@/contexts/VoiceServiceContext';
import { useVerifiedPhoneNumbersQuery } from '@/hooks/useVerifiedPhoneNumbers';
import { useTranslation } from 'react-i18next';
import { pricingQueries } from '@/services/pricingApi';
import type { TFunction } from 'i18next';
import type { CallerIdMode } from '@/services/voice/types';
import type { CountryRate } from '@/types/pricing';
import Keypad from './Keypad.tsx';
import { PhoneLayout, PhoneHeader, PhoneDisplay } from './Layout.tsx';
import { BalanceDisplay } from './BalanceDisplay';
import { CallStatus, type StatusConfig } from './CallStatus';
import { CountrySelector } from './CountrySelector';
import { AudioSettings } from './AudioSettings';
import StripeTopUpModal from '@/components/StripeTopUpModal';
import { LoginModal } from '@/components/auth/LoginModal';

interface CallerIdToggleProps {
    mode: CallerIdMode;
    verifiedNumbers: string[];
    selectedNumber: string | null;
    onSelect: (mode: CallerIdMode, number: string | null) => void;
    disabled: boolean;
}

const CallerIdToggle: React.FC<CallerIdToggleProps> = ({
    mode,
    verifiedNumbers,
    selectedNumber,
    onSelect,
    disabled,
}) => {
    const { t } = useTranslation('app');
    const activeNumber = mode === 'verified' ? (selectedNumber ?? verifiedNumbers[0] ?? null) : null;

    return (
        <div className={cn(disabled && 'opacity-50 pointer-events-none')}>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background px-3 py-1.5 text-xs font-medium shadow-sm transition-colors hover:bg-muted whitespace-nowrap"
                    >
                        <User className="w-3.5 h-3.5 shrink-0" />
                        <span className={cn('max-w-[8rem] truncate', activeNumber && 'font-mono')}>
                            {activeNumber ?? (mode === 'anonymous' ? t('dialer.callerIdMode.anonymous') : t('dialer.callerIdMode.any'))}
                        </span>
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                    {verifiedNumbers.map((num) => (
                        <DropdownMenuItem
                            key={num}
                            className="flex items-center gap-2 text-xs font-mono"
                            onSelect={() => onSelect('verified', num)}
                        >
                            <ShieldCheck className={cn('w-3.5 h-3.5 shrink-0', num === activeNumber ? 'text-foreground' : 'text-muted-foreground')} />
                            <span className={num === activeNumber ? 'font-medium' : ''}>{num}</span>
                        </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem
                        className="flex flex-col items-start gap-1 text-xs py-3"
                        onSelect={() => onSelect('anonymous', null)}
                    >
                        <div className="flex items-center gap-2">
                            <EyeOff className={cn('w-3.5 h-3.5', mode === 'anonymous' ? 'text-foreground' : 'text-muted-foreground')} />
                            <span className={mode === 'anonymous' ? 'font-medium' : ''}>{t('dialer.callerIdMode.anonymous')}</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-relaxed pl-5.5">
                            {t('dialer.callerIdMode.anonymousDisclaimer')}
                        </p>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        className="flex flex-col items-start gap-1 text-xs py-3"
                        onSelect={() => onSelect('cheapest', null)}
                    >
                        <div className="flex items-center gap-2">
                            <Zap className={cn('w-3.5 h-3.5', mode === 'cheapest' ? 'text-foreground' : 'text-muted-foreground')} />
                            <span className={mode === 'cheapest' ? 'font-medium' : ''}>{t('dialer.callerIdMode.any')}</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-relaxed pl-5.5">
                            {t('dialer.callerIdMode.anyDisclaimer')}
                        </p>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};


interface PhoneDialerProps {
    /** Additional CSS classes */
    className?: string;
    /** Phone number to pre-fill (from parent, e.g. contact/recent selection) */
    prefillNumber?: string | null;
    /** Called after prefillNumber has been consumed */
    onPrefillConsumed?: () => void;
}

function formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

function getStatusConfig(
    status: string,
    duration: number,
    t: TFunction
): StatusConfig {
    switch (status) {
        case 'validating':
            return { label: t('dialer.validating'), icon: PhoneOutgoing, pulse: true, iconColor: 'text-blue-600', textColor: 'text-blue-700' };
        case 'connecting':
            return { label: t('dialer.connecting'), icon: PhoneOutgoing, pulse: true, iconColor: 'text-amber-600', textColor: 'text-amber-700' };
        case 'ringing':
            return { label: t('dialer.ringing'), icon: PhoneOutgoing, pulse: true, iconColor: 'text-blue-600', textColor: 'text-blue-700' };
        case 'in-progress':
            return { label: t('dialer.connected', { duration: formatDuration(duration) }), icon: Phone, pulse: false, iconColor: 'text-green-600', textColor: 'text-green-700' };
        case 'reconnecting':
            return { label: t('dialer.reconnecting'), icon: PhoneOutgoing, pulse: true, iconColor: 'text-orange-600', textColor: 'text-orange-700' };
        default:
            return { label: t('dialer.statusUnknown'), icon: Phone, pulse: false, iconColor: 'text-red-600', textColor: 'text-red-700' };
    }
}

const BackspaceButton: React.FC<{ onBackspace: () => void; onClear: () => void; disabled?: boolean }> = ({ onBackspace, onClear, disabled }) => {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const longPressTriggeredRef = useRef(false);

    const handlePressStart = useCallback(() => {
        if (disabled) return;
        longPressTriggeredRef.current = false;
        timerRef.current = setTimeout(() => {
            longPressTriggeredRef.current = true;
            onClear();
        }, 500);
    }, [disabled, onClear]);

    const handlePressEnd = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (!longPressTriggeredRef.current && !disabled) {
            onBackspace();
        }
    }, [disabled, onBackspace]);

    const handlePressCancel = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        longPressTriggeredRef.current = false;
    }, []);

    return (
        <Button
            variant="ghost"
            className="w-[68px] h-[68px] short:w-[54px] short:h-[54px] rounded-full bg-muted hover:bg-muted/80 border active:scale-95 transition-all duration-150"
            disabled={disabled}
            onPointerDown={handlePressStart}
            onPointerUp={handlePressEnd}
            onPointerCancel={handlePressCancel}
            onPointerLeave={handlePressCancel}
            onClick={(e) => e.preventDefault()}
        >
            <Delete className="w-5 h-5 text-muted-foreground" />
        </Button>
    );
};

/**
 * PhoneDialer - Integrated dialer and call management
 */
const PhoneDialer: React.FC<PhoneDialerProps> = ({ className, prefillNumber, onPrefillConsumed }) => {
    const { t } = useTranslation('app');
    const location = useLocation();
    const navigate = useNavigate();
    const { stripePublishableKey } = useRootConfig()

    const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState<'topup' | 'call' | null>(null);
    const [dtmfDigits, setDtmfDigits] = useState('');
    const [showKeypad, setShowKeypad] = useState(true);
    const [showAudioSettings, setShowAudioSettings] = useState(false);
    const [comboboxOpen, setComboboxOpen] = useState(false);

    const queryParams = useQueryParams();
    const phoneParamProcessedRef = useRef(false);

    const {
        callState,
        deviceStatus,
        networkQuality,
        isReady: isVoiceReady,
        startCall,
        endCall,
        sendDTMF,
        isMock,
        isMuted,
        audioDevices,
        toggleMute,
        setInputDevice,
        setOutputDevice,
    } = useVoiceServiceContext();

    const isCallActive = callState.status === 'validating' || callState.status === 'connecting' || callState.status === 'ringing' || callState.status === 'in-progress' || callState.status === 'reconnecting';
    const isCallIdle = callState.status === 'idle';

    const { data: pricingData, isLoading: isPricingLoading } = useQuery(pricingQueries.pricing());

    const {
        displayValue: phoneNumber,
        e164: e164Number,
        isValid: isValidNumber,
        exampleNumber,
        selectedCountry,
        setCountry,
        detectCountry,
        handleDigit: baseHandleDigit,
        handleBackspace,
        handleClear,
        handlePaste: baseHandlePaste,
        setValue: setPhoneValue,
    } = usePhoneInput({ pricingCountries: pricingData?.countries });

    // CountrySelector expects CountryRate; our selectedCountry carries the original in .meta
    const selectedCountryRate = selectedCountry?.meta ?? null;
    const allCountryRates = pricingData?.countries;

    const { isAuthenticated } = useAuth();
    const { data: balanceResponse, isLoading: isBalanceLoading } = useQuery(accountQueries.balance());
    const { data: accountData } = useQuery(accountQueries.me());
    const { data: autoTopupConfig } = useQuery(autoTopupQueries.config());
    const isVerified = accountData?.account?.verified ?? true;

    // Verified phone numbers for caller ID toggle
    const { data: verifiedPhoneNumbersData } = useVerifiedPhoneNumbersQuery();
    const verifiedNumberStrings = verifiedPhoneNumbersData?.items.filter(n => n.verified).map(n => n.phoneNumber) ?? [];
    const hasVerifiedNumbers = isAuthenticated && verifiedNumberStrings.length > 0;

    // Use new useSessionSetting hook for caller ID with session override support
    const {
        value: callerIdSetting,
        setSessionOverride
    } = useSessionSetting('call:caller_id_mode');

    // Parse the setting value
    const isPoolMode = callerIdSetting === 'cheapest' || callerIdSetting === 'anonymous';
    const callerIdMode: CallerIdMode = callerIdSetting === 'anonymous' ? 'anonymous' : isPoolMode ? 'cheapest' : 'verified';
    const callerIdNumber: string | null = isPoolMode ? null : callerIdSetting;

    // Default to anonymous if the user has no verified numbers
    const effectiveCallerIdMode: CallerIdMode = hasVerifiedNumbers ? callerIdMode : 'anonymous';
    const effectiveCallerIdNumber = isPoolMode ? null : ((callerIdNumber ?? verifiedNumberStrings[0]) || null);

    const handleCallerIdSelect = useCallback((newMode: CallerIdMode, number: string | null) => {
        const value = newMode === 'cheapest' ? 'cheapest' : newMode === 'anonymous' ? 'anonymous' : (number ?? 'anonymous');
        setSessionOverride(value);
    }, [setSessionOverride]);

    const handleCountryChange = useCallback((country: CountryRate) => {
        // Map back from CountryRate to PhoneCountryOption via detectCountry or direct build
        const opt = detectCountry(country.dialingCode) ?? {
            countryCode: country.countryCode,
            dialingCode: country.dialingCode,
            country: country.country,
            dialingCodePreferred: country.dialingCodePreferred,
            meta: country,
        };
        setCountry(opt);
        setComboboxOpen(false);
    }, [detectCountry, setCountry]);

    const handleNumberInput = useCallback((digit: string) => {
        if (isCallActive) return;
        // Tap 0 when field is empty and no country selected → enter + (like standard phone dialers)
        const effectiveDigit = digit === '0' && phoneNumber === '' && !selectedCountry ? '+' : digit;
        baseHandleDigit(effectiveDigit);
    }, [isCallActive, phoneNumber, selectedCountry, baseHandleDigit]);

    const handlePasteNumber = useCallback((raw: string) => {
        if (isCallActive) return;
        baseHandlePaste(raw);
    }, [isCallActive, baseHandlePaste]);

    const handlePostLogin = useCallback(({ isNewAccount }: { isNewAccount: boolean }) => {
        if (pendingAction === 'topup' || isNewAccount) {
            setIsTopUpModalOpen(true);
        } else if (pendingAction === 'call' && e164Number.trim() && isCallIdle) {
            startCall(e164Number, { callerIdMode: effectiveCallerIdMode, callerIdNumber: effectiveCallerIdNumber ?? undefined });
        }
        setPendingAction(null);
    }, [pendingAction, e164Number, isCallIdle, startCall, effectiveCallerIdMode, effectiveCallerIdNumber]);

    const handleCall = useCallback(() => {
        if (!isAuthenticated) {
            setPendingAction('call');
            setIsLoginModalOpen(true);
            return;
        }
        if (e164Number.trim() && isCallIdle) {
            startCall(e164Number, { callerIdMode: effectiveCallerIdMode, callerIdNumber: effectiveCallerIdNumber ?? undefined }).catch(() => {
                // Error is already handled inside startCall (toast + state update)
            });
        }
    }, [isAuthenticated, e164Number, isCallIdle, startCall, effectiveCallerIdMode, effectiveCallerIdNumber]);

    const handleDTMFInput = useCallback((digit: string) => {
        if (!isCallActive) return;
        sendDTMF(digit);
        setDtmfDigits(prev => prev + digit);
    }, [isCallActive, sendDTMF]);

    const handleKeypadInput = useCallback((digit: string) => {
        if (isCallActive) {
            handleDTMFInput(digit);
        } else {
            handleNumberInput(digit);
        }
    }, [isCallActive, handleDTMFInput, handleNumberInput]);

    useEffect(() => {
        if (isCallActive) {
            setShowKeypad(false);
        } else {
            setDtmfDigits('');
        }
    }, [isCallActive]);

    const queryClient = useQueryClient();

    useEffect(() => {
        if (callState.status === 'ended') {
            queryClient.invalidateQueries({ queryKey: accountQueries.contactsBase });
            queryClient.invalidateQueries({ queryKey: accountQueries.contactsInfiniteBase });
            queryClient.invalidateQueries({ queryKey: accountQueries.callHistoryBase });
            queryClient.invalidateQueries({ queryKey: accountQueries.callHistoryInfiniteBase });
        }
    }, [callState.status, queryClient]);

    useKeyboardHandler({ handleNumberInput, handleBackspace, handleCall, handleClear, handlePasteNumber: handlePasteNumber, phoneNumber });

    useEffect(() => {
        if (!pricingData?.countries?.length) return;
        const countryParam = queryParams.get('country');
        if (countryParam) {
            const normalized = countryParam.trim().toUpperCase();
            const match = pricingData.countries.find(c =>
                c.countryCode.toUpperCase() === normalized || c.country.toUpperCase() === normalized
            );
            if (match) {
                setCountry({
                    countryCode: match.countryCode,
                    dialingCode: match.dialingCode,
                    country: match.country,
                    dialingCodePreferred: match.dialingCodePreferred,
                    meta: match,
                });
            }
        }
    }, [pricingData?.countries, queryParams, setCountry]);

    useEffect(() => {
        if (!prefillNumber) return;
        baseHandlePaste(prefillNumber);
        onPrefillConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefillNumber]);

    useEffect(() => {
        if (isPricingLoading) return;
        const phoneParam = queryParams.get('phone');
        if (!phoneParam || phoneParamProcessedRef.current) return;
        phoneParamProcessedRef.current = true;
        handleClear();
        Array.from(phoneParam.replace(/\s+/g, '')).forEach(char => baseHandleDigit(char));

        const params = new URLSearchParams(location.searchStr);
        params.delete('phone');
        const remainingSearch = Object.fromEntries(params.entries());
        navigate({ to: '.', search: Object.keys(remainingSearch).length ? remainingSearch : undefined, replace: true });
    }, [isPricingLoading, queryParams, handleClear, baseHandleDigit, location, navigate]);

    if (isPricingLoading) {
        return (
            <PhoneLayout className={className}>
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
                        <p className="text-muted-foreground">{t('dialer.loadingCountries')}</p>
                    </div>
                </div>
            </PhoneLayout>
        );
    }

    const statusConfig = isCallActive
        ? getStatusConfig(callState.status, callState.duration || 0, t)
        : null;

    return (
        <PhoneLayout className={className}>
            {/* Balance */}
            <PhoneHeader className="text-center">
                <div className="flex flex-col gap-2">
                    <div className="flex items-start justify-center gap-3">
                        <div className="flex flex-wrap items-center gap-3 justify-center">
                            {/* Auto top-up indicator */}
                            {isAuthenticated && autoTopupConfig?.enabled && (
                                <div className="flex justify-center">
                                    <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300 bg-emerald-50 gap-1">
                                        <RefreshCw className="h-3 w-3" />
                                    </Badge>
                                </div>
                            )}
                            <BalanceDisplay
                                isAuthenticated={isAuthenticated}
                                isBalanceLoading={isBalanceLoading}
                                accountBalance={balanceResponse?.accountBalance}
                                isVerified={isVerified}
                                isCallActive={isCallActive}
                                onTopUp={() => setIsTopUpModalOpen(true)}
                                onLogin={() => { setPendingAction('topup'); setIsLoginModalOpen(true); }}
                            />
                            {isAuthenticated && (
                                <CallerIdToggle
                                    mode={effectiveCallerIdMode}
                                    verifiedNumbers={verifiedNumberStrings}
                                    selectedNumber={effectiveCallerIdNumber}
                                    onSelect={handleCallerIdSelect}
                                    disabled={isCallActive}
                                />
                            )}
                        </div>
                    </div>
                    {/* Device status indicator */}
                    {!isMock && deviceStatus !== 'registered' && (
                        <div className="flex justify-center">
                            {deviceStatus === 'unregistered' && (
                                <Badge variant="outline" className="text-xs text-red-600 border-red-300 bg-red-50">
                                    <div className="rounded-full w-1.5 h-1.5 mr-1.5 bg-red-500" />
                                    Offline
                                </Badge>
                            )}
                            {deviceStatus === 'error' && (
                                <Badge variant="outline" className="text-xs text-red-600 border-red-300 bg-red-50">
                                    <div className="rounded-full w-1.5 h-1.5 mr-1.5 bg-red-500" />
                                    Service Error
                                </Badge>
                            )}
                            {deviceStatus === 'registering' && (
                                <Badge variant="outline" className="text-xs text-muted-foreground">
                                    <div className="rounded-full w-1.5 h-1.5 mr-1.5 bg-muted-foreground animate-pulse" />
                                    Connecting
                                </Badge>
                            )}
                        </div>
                    )}
                </div>
            </PhoneHeader>

            {/* Phone Number Display */}
            <PhoneDisplay>
                {dtmfDigits ? (
                    <div className="font-mono text-lg break-all text-center w-full">{dtmfDigits}</div>
                ) : (
                <div className="flex items-center gap-2 w-full">
                    <CountrySelector
                        selectedCountry={selectedCountryRate}
                        countries={allCountryRates}
                        open={comboboxOpen}
                        onOpenChange={setComboboxOpen}
                        onSelect={handleCountryChange}
                        disabled={isCallActive}
                    />
                    <div
                        className="flex-1 min-w-0 cursor-text select-none"
                        onClick={async () => {
                            if (isCallActive) return;
                            try {
                                const text = await navigator.clipboard.readText();
                                if (text) handlePasteNumber(text);
                            } catch {
                                // Clipboard permission denied or unavailable
                            }
                        }}
                    >
                        <div className="font-mono text-lg break-all">
                            {phoneNumber ? (
                                <span>{phoneNumber}</span>
                            ) : !isCallActive ? (
                                <span className="text-muted-foreground/40">{exampleNumber || t('dialer.exampleNumber')}</span>
                            ) : null}
                        </div>
                    </div>
                </div>
                )}
            </PhoneDisplay>

            {/* Call Status + Network Quality */}
            <CallStatus
                statusConfig={statusConfig}
                isVisible={isCallActive}
                networkQuality={networkQuality}
            />

            {/* Keypad + Actions */}
            <div className="flex flex-col justify-center mt-4 short:mt-2">
                <div className="grid grid-cols-3 gap-x-5 gap-y-4 short:gap-x-3 short:gap-y-2 w-fit mx-auto">
                    {/* Rows 1–3: keypad digits or invisible placeholders */}
                    {!isCallActive || showKeypad ? (
                        <Keypad onInput={handleKeypadInput} />
                    ) : (
                        Array.from({ length: 9 }, (_, i) => (
                            <div key={i} className="w-[68px] h-[68px] short:w-[54px] short:h-[54px]" />
                        ))
                    )}

                    {/* Row 4 (call only): mute | keypad-toggle | audio */}
                    {isCallActive && !showKeypad && (
                        <>
                            <Button
                                onClick={toggleMute}
                                variant="ghost"
                                className={cn(
                                    "w-[68px] h-[68px] short:w-[54px] short:h-[54px] rounded-full bg-muted hover:bg-muted/80 border active:scale-95 transition-all duration-150",
                                    isMuted && "bg-destructive/10 hover:bg-destructive/20 border-destructive",
                                )}
                            >
                                {isMuted
                                    ? <MicOff className="w-5 h-5 text-destructive" />
                                    : <Mic className="w-5 h-5 text-muted-foreground" />
                                }
                            </Button>
                            <Button
                                onClick={() => setShowKeypad(true)}
                                variant="ghost"
                                className="w-[68px] h-[68px] short:w-[54px] short:h-[54px] rounded-full bg-muted hover:bg-muted/80 border active:scale-95 transition-all duration-150"
                            >
                                <Grid3X3 className="w-5 h-5 text-muted-foreground" />
                            </Button>
                            <AudioSettings
                                open={showAudioSettings}
                                onOpenChange={setShowAudioSettings}
                                audioDevices={audioDevices}
                                onInputChange={setInputDevice}
                                onOutputChange={setOutputDevice}
                            />
                        </>
                    )}

                    {/* Bottom row: action buttons */}
                    {!isCallActive ? (
                        // Idle: spacer | call | backspace
                        <>
                            <div className="w-[68px] h-[68px] short:w-[54px] short:h-[54px]" />
                            <Button
                                onClick={handleCall}
                                className="w-[68px] h-[68px] short:w-[54px] short:h-[54px] rounded-full bg-primary hover:bg-primary/90 active:scale-95 transition-all duration-150"
                                disabled={!isValidNumber || !phoneNumber.trim() || (isAuthenticated && !isVoiceReady)}
                            >
                                <PhoneCall className="w-5 h-5" />
                            </Button>
                            <BackspaceButton
                                onBackspace={handleBackspace}
                                onClear={handleClear}
                                disabled={!phoneNumber}
                            />
                        </>
                    ) : showKeypad ? (
                        // Call + keypad shown: spacer | end-call | hide-keypad (text)
                        <>
                            <div className="w-[68px] h-[68px] short:w-[54px] short:h-[54px]" />
                            <Button
                                onClick={endCall}
                                className="w-[68px] h-[68px] short:w-[54px] short:h-[54px] rounded-full bg-destructive hover:bg-destructive/90 active:scale-95 transition-all duration-150"
                            >
                                <PhoneOff className="w-5 h-5" />
                            </Button>
                            <div className="w-[68px] h-[68px] short:w-[54px] short:h-[54px] flex items-center justify-center">
                                <button
                                    onClick={() => { setShowKeypad(false); setDtmfDigits(''); }}
                                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    Hide keypad
                                </button>
                            </div>
                        </>
                    ) : (
                        // Call + keypad hidden: spacer | end-call
                        <>
                            <div className="w-[68px] h-[68px] short:w-[54px] short:h-[54px]" />
                            <Button
                                onClick={endCall}
                                className="w-[68px] h-[68px] short:w-[54px] short:h-[54px] rounded-full bg-destructive hover:bg-destructive/90 active:scale-95 transition-all duration-150"
                            >
                                <PhoneOff className="w-5 h-5" />
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {isMock && (
                <p className="text-center text-[11px] text-muted-foreground/50 mt-3 select-none">
                    mock mode
                </p>
            )}

            <StripeTopUpModal
                isOpen={isTopUpModalOpen}
                onClose={() => setIsTopUpModalOpen(false)}
                stripePublishableKey={stripePublishableKey}
            />
            <LoginModal
                isOpen={isLoginModalOpen}
                onClose={() => setIsLoginModalOpen(false)}
                onSuccess={handlePostLogin}
            />
        </PhoneLayout>
    );
};

export default PhoneDialer;
